import { useCallback, useEffect, useRef, useState } from "react";
import { errorId, toErrorList, type FieldProps } from "@rjsf/utils";
import { createDocumentContextClient, DocumentContextError, type DocumentContextItem } from "../../lib/documentContext";
import type { DocumentContextState } from "../../hooks/useDocumentContext";
import type { WorkSettingsData } from "../../../../shared/work-settings";
import { useLocale } from "../../i18n/LocaleProvider";
import { FieldInfo } from "./FieldInfo";

export interface HostContext {
  token: string | null;
  orgId: string | null;
  revitVersion: string | null;
  sessionId: string | null;
}

export interface ActiveContextStatus { loading: boolean; error: string }

/** Read the selected connection afresh; the shell's host list can be one poll behind. */
export async function loadActiveContextOptions(host: HostContext, type: string, t: (key: string) => string) {
  if (!host.token || !host.revitVersion || !host.sessionId || !window.bimgo?.hosts)
    throw new Error(t("workSettings.context.connect"));
  const client = createDocumentContextClient({ token: host.token, organizationId: host.orgId });
  const readSource = async () => {
    const [instances, keys] = await Promise.all([
      window.bimgo!.hosts!.list(),
      client.keys(host.revitVersion!).catch(error => {
        if (error instanceof DocumentContextError && error.status === 404)
          throw new Error(t("workSettings.context.indexUnavailable"));
        throw error;
      }),
    ]);
    const selected = instances.find(item => item.host_kind === "revit" && item.sessionId === host.sessionId);
    if (!selected || (selected.hostVersion ?? selected.revitVersion) !== host.revitVersion)
      throw new Error(t("workSettings.context.hostChanged"));
    if (!selected.documentId || !keys.documentId || selected.documentId !== keys.documentId || !keys.lastUpdated)
      throw new Error(t("workSettings.context.indexUnavailable"));
    return keys;
  };
  const source = await readSource();
  const items = await client.typeItems(host.revitVersion, type);
  const after = await readSource();
  if (source.documentId !== after.documentId || source.lastUpdated !== after.lastUpdated ||
      source.documentName !== after.documentName || source.documentPath !== after.documentPath)
    throw new Error(t("workSettings.context.modelChanged"));
  return { source, items };
}

/** The option that leaves the item to the agent (`ui:options.agentChoice`). */
const AGENT_CHOICE = "__agent__";

/** BuiltInParameter.RBS_PIPE_SYSTEM_CALCULATION_PARAM identifies piping without localised names. */
function selectableItems(items: DocumentContextItem[], pipingOnly: boolean) {
  return pipingOnly ? items.filter(item => item.typeParameters?.some(parameter =>
    parameter.definition === "BuiltIn" && parameter.id === "-1140335")) : items;
}

/**
 * A Revit item picked from the selected instance's index. The index itself — its
 * status, auto-build and rebuild — is the Revit Database panel's (`documentContext`);
 * this field only reads its items and checks the index is of the selected Revit's
 * open document.
 */
export function ActiveContextField(props: FieldProps) {
  const { t } = useLocale();
  const { sessionId, revitVersion, documentContext, contextReload, onContextStatus } = props.registry.formContext as HostContext & {
    documentContext?: DocumentContextState;
    contextReload?: number;
    onContextStatus?: (field: string, status: ActiveContextStatus) => void;
  };
  const name = props.name;
  const options = props.uiSchema?.["ui:options"];
  const type = String(options?.contextType ?? "Level");
  const agentChoice = options?.agentChoice === true;
  const pipingOnly = options?.pipingOnly === true;
  const readonly = props.readonly;
  const documentId = documentContext?.documentId ?? null;
  const lastUpdated = documentContext?.lastUpdated ?? null;
  const fetchTypeItems = documentContext?.fetchTypeItems;
  const sourceKey = JSON.stringify([sessionId, revitVersion, documentId, lastUpdated, type]);
  const latestSourceKey = useRef(sourceKey);
  latestSourceKey.current = sourceKey;
  const [loaded, setLoaded] = useState<{ items: DocumentContextItem[]; sourceKey: string } | null>(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const epoch = useRef(0);
  const load = useCallback(async () => {
    const request = ++epoch.current;
    setLoaded(null);
    setMessage("");
    // Nothing to read until the index is there; the panel's blocker says why.
    if (readonly || !fetchTypeItems || !documentId || !lastUpdated) return;
    setLoading(true);
    onContextStatus?.(name, { loading: true, error: "" });
    const current = () => request === epoch.current && sourceKey === latestSourceKey.current;
    try {
      const instances = await window.bimgo?.hosts?.list() ?? [];
      const selected = instances.find(item => item.host_kind === "revit" && item.sessionId === sessionId);
      if (!selected || (selected.hostVersion ?? selected.revitVersion) !== revitVersion)
        throw new Error(t("workSettings.context.hostChanged"));
      // The index describes another document than the one open: it rebuilds on the switch.
      if (!selected.documentId || selected.documentId !== documentId)
        throw new Error(t("workSettings.context.indexUnavailable"));
      const items = selectableItems(await fetchTypeItems(type), pipingOnly);
      if (current()) {
        setLoaded({ items, sourceKey });
        onContextStatus?.(name, { loading: false, error: "" });
        if (!items.length && !agentChoice) setMessage(t("workSettings.context.noItems"));
      }
    } catch (error) {
      if (current()) {
        const text = error instanceof Error ? error.message : String(error);
        setMessage(text);
        onContextStatus?.(name, { loading: false, error: text });
      }
    } finally {
      if (current()) setLoading(false);
    }
  }, [readonly, fetchTypeItems, documentId, lastUpdated, sessionId, revitVersion, type, sourceKey, agentChoice, pipingOnly, t, name, onContextStatus]);
  useEffect(() => {
    void load();
    return () => { epoch.current += 1; };
  }, [load, contextReload]);

  const ready = loaded?.sourceKey === sourceKey;
  const items = ready ? loaded.items : [];
  const value = props.formData;
  const chosenByAgent = agentChoice && value?.agentChoice === true;
  const valid = chosenByAgent || (ready && value?.documentId === documentId && value?.lastUpdated === lastUpdated &&
    value?.sessionId === sessionId && value?.type === type && items.some(item => item.id === value?.id));
  const modelName = readonly ? value?.documentName : documentContext?.documentName;
  const hasErrors = toErrorList(props.errorSchema ?? {}).length > 0;
  const hint = message || (value?.id && ready && !valid ? t("workSettings.context.reselect") : "") ||
    (hasErrors ? t("workSettings.validation.option") : "");
  const names = new Set<string>();
  const duplicates = new Set<string>();
  for (const item of items) { if (names.has(item.name)) duplicates.add(item.name); names.add(item.name); }
  const selectedValue = chosenByAgent ? AGENT_CHOICE : readonly ? value?.id ?? "" : valid ? value.id : "";
  return (
    <div className="form-group">
      <div className="work-settings-context-heading">
        <div className="work-settings-label-row">
          <label htmlFor={props.fieldPathId.$id}>{props.schema.title}</label>
          <FieldInfo about={props.schema.description} schema={props.schema} />
        </div>
        {!readonly && <button type="button" className="icon-btn work-settings-context-refresh"
          disabled={loading || props.disabled || !documentContext || !sessionId || !revitVersion}
          aria-label={`${props.schema.title}: ${t("workSettings.refresh")}`} title={t("workSettings.refresh")}
          onClick={() => void documentContext?.refresh().then(load)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 7v5h-5M4 17v-5h5M6.1 7a7 7 0 0 1 11.6-1L20 9M4 15l2.3 3A7 7 0 0 0 17.9 17" /></svg>
        </button>}
      </div>
      {modelName && <p className="work-settings-context-source">{modelName}</p>}
      <select id={props.fieldPathId.$id}
        aria-invalid={hasErrors || undefined}
        aria-describedby={hint ? errorId(props.fieldPathId) : undefined}
        disabled={!ready || loading || (!items.length && !agentChoice) || props.disabled || readonly}
        value={selectedValue}
        onChange={(event) => {
          if (event.target.value === AGENT_CHOICE) {
            props.onChange({ type, agentChoice: true }, props.fieldPathId.path);
            return;
          }
          const item = items.find(entry => entry.id === event.target.value);
          props.onChange(item && documentId && lastUpdated ? {
            id: item.id, name: item.name, type, sessionId, revitVersion,
            documentId,
            documentName: documentContext?.documentName ?? "",
            documentPath: documentContext?.documentPath ?? "",
            lastUpdated,
            // Explicit: RJSF merges the schema default (`agentChoice: true`) into
            // any value that lacks the key, which would undo the pick.
            ...(agentChoice ? { agentChoice: false } : {}),
          } : undefined, props.fieldPathId.path);
        }}>
        {!agentChoice && <option value="">{t(loading ? "common.loading" : "workSettings.context.select")}</option>}
        {agentChoice && <option value={AGENT_CHOICE}>{t("workSettings.context.agentChoice")}</option>}
        {readonly && !chosenByAgent && value?.id && <option value={value.id}>{value.name ?? value.id}</option>}
        {!readonly && items.map(item => <option key={item.id} value={item.id}>
          {item.name}{duplicates.has(item.name) ? ` (${item.id})` : ""}
        </option>)}
      </select>
      {!readonly && hint && (!onContextStatus || ready) && <p id={errorId(props.fieldPathId)} role="alert">{hint}</p>}
    </div>
  );
}

export async function validateActiveContext(
  data: WorkSettingsData,
  uiSchema: Record<string, unknown>,
  host: HostContext,
  t: (key: string) => string,
): Promise<void> {
  let documentId: string | undefined;
  for (const [key, config] of Object.entries(uiSchema)) {
    if (!config || typeof config !== "object" || (config as Record<string, unknown>)["ui:field"] !== "ActiveContext") continue;
    const value = data[key];
    if (!value || typeof value !== "object" || Array.isArray(value)) continue;
    // Left to the agent: nothing in the model to check.
    if (value.agentChoice === true) continue;
    if (typeof value.documentId !== "string" || !value.documentId || (documentId && value.documentId !== documentId))
      throw new Error(t("workSettings.context.modelChangedReselect"));
    documentId = value.documentId;
    if (!host.token || !host.revitVersion || !host.sessionId ||
        value.sessionId !== host.sessionId || value.revitVersion !== host.revitVersion)
      throw new Error(t("workSettings.context.hostChanged"));
    const options = (config as Record<string, unknown>)["ui:options"] as Record<string, unknown> | undefined;
    const expectedType = String(options?.contextType ?? "Level");
    if (value.type !== expectedType) throw new Error(t("workSettings.context.typeMismatch"));
    const { source, items } = await loadActiveContextOptions(host, expectedType, t);
    if (source.documentId !== value.documentId || source.lastUpdated !== value.lastUpdated ||
        (source.documentName ?? "") !== value.documentName || (source.documentPath ?? "") !== value.documentPath)
      throw new Error(t("workSettings.context.modelChangedReselect"));
    if (!selectableItems(items, options?.pipingOnly === true).some(item => item.id === value.id && item.name === value.name))
      throw new Error(t("workSettings.context.itemUnavailable"));
  }
}
